/**
 * Generic catalog prefetching system
 *
 * This module provides a centralized way to prefetch catalog JSON files for each range.
 * Catalogs are loaded in the background when landing page renders, so they're
 * already available when the user navigates to the planner.
 *
 * To add a new range:
 * 1. Add an entry to the catalogPrefetchers object below
 * 2. Return an array of dynamic imports for that range's catalog files
 * 3. Look at the range's plannerConfig.ts to find which catalogs it loads
 */

type CatalogPrefetcher = () => Promise<any>[];

const catalogPrefetchers: Record<string, CatalogPrefetcher> = {
  pax: () => [
    import('@inter-ikea-storageone/range-pax/data/catalog.json'),
    import('@inter-ikea-storageone/range-pax/data/interior-suggestions/catalog.json'),
    import('@inter-ikea-storageone/range-handles/data/catalog.json'),
    import('@inter-ikea-storageone/range-bedroom-storage-propping/data/catalog.json')
  ],
  besta: () => [
    import('@inter-ikea-storageone/range-besta/data/catalog.json'),
    import('@inter-ikea-storageone/range-besta/data/front-layouts/catalog.json'),
    import('@inter-ikea-storageone/range-besta/data/media/catalog.json'),
    import('@inter-ikea-storageone/range-besta-eket-propping/data/catalog.json'),
    import('@inter-ikea-storageone/range-eket/data/catalog.json'),
    import('@inter-ikea-storageone/range-handles/data/catalog.json')
  ],
  kallax: () => [import('@inter-ikea-storageone/storagetwo-kallax/rangeConfig/catalog.json')],
  platsa: () => [
    import('@inter-ikea-storageone/range-platsa/data/catalog.json'),
    import('@inter-ikea-storageone/range-platsa/data/standard-hinges/catalog.json'),
    import('@inter-ikea-storageone/range-platsa/data/interior-suggestions/catalog.json'),
    import('@inter-ikea-storageone/range-platsa/data/front-layouts/catalog.json'),
    import('@inter-ikea-storageone/range-handles/data/catalog.json'),
    import('@inter-ikea-storageone/range-bedroom-storage-propping/data/catalog.json')
  ],
  metod: () => [
    import('@inter-ikea-storageone/range-metod/data/metod_catalog.json'),
    import('@inter-ikea-storageone/range-metod/data/kitchen-worktops_catalog.json'),
    import('@inter-ikea-storageone/range-handles/data/catalog.json'),
    import('@inter-ikea-storageone/range-kitchen/data/kitchen-appliances_catalog.json'),
    import('@inter-ikea-storageone/range-kitchen/data/kitchen-sinks_catalog.json'),
    import('@inter-ikea-storageone/range-kitchen/data/kitchen-taps_catalog.json')
  ],
  knoxhult: () => [
    import('@inter-ikea-storageone/range-knoxhult/data/knoxhult_catalog.json'),
    import('@inter-ikea-storageone/range-handles/data/catalog.json'),
    import('@inter-ikea-storageone/range-kitchen/data/kitchen-appliances_catalog.json'),
    import('@inter-ikea-storageone/range-kitchen/data/kitchen-sinks_catalog.json'),
    import('@inter-ikea-storageone/range-kitchen/data/kitchen-taps_catalog.json')
  ],
  billy: () => [import('@inter-ikea-storageone/range-billy/data/catalog.json')],
  smastad: () => [
    import('@inter-ikea-storageone/range-smastad/data/catalog.json'),
    import('@inter-ikea-storageone/range-smastad/data/interior-suggestions/catalog.json'),
    import('@inter-ikea-storageone/range-smastad/data/front-layouts/catalog.json'),
    import('@inter-ikea-storageone/range-smastad-propping/data/catalog.json')
  ],
  jonaxel: () => [
    import('@inter-ikea-storageone/range-jonaxel/data/catalog.json'),
    import('@inter-ikea-storageone/range-jonaxel/data/interior-suggestions/catalog.json'),
    import('@inter-ikea-storageone/range-bedroom-storage-propping/data/catalog.json')
  ],
  eket: () => [
    import('@inter-ikea-storageone/range-eket/data/catalog.json'),
    import('@inter-ikea-storageone/range-besta/data/media/catalog.json'),
    import('@inter-ikea-storageone/range-besta-eket-propping/data/catalog.json')
  ],
  ivar: () => [
    import('@inter-ikea-storageone/storagetwo-ivar/rangeConfig/catalog.json'),
    import('@inter-ikea-storageone/range-ivar/data/interior-suggestions/catalog.json')
  ],
  elvarli: () => [import('@inter-ikea-storageone/storagetwo-elvarli/rangeConfig/catalog.json')],
  boaxel: () => [
    import('@inter-ikea-storageone/storagetwo-boaxel/rangeConfig/catalog.json'),
    import('@inter-ikea-storageone/range-bedroom-storage-propping/data/catalog.json')
  ],
  bror: () => [import('@inter-ikea-storageone/storagetwo-bror/rangeConfig/catalog.json')],
  aurdal: () => [
    import('@inter-ikea-storageone/range-aurdal/data/catalog.json'),
    import('@inter-ikea-storageone/range-bedroom-storage-propping/data/catalog.json')
  ],
  ladmakare: () => [import('@inter-ikea-storageone/range-ladmakare/data/catalog.json')],
  lastare: () => [
    import('@inter-ikea-storageone/range-lastare/data/catalog.json'),
    import('@inter-ikea-storageone/range-handles/data/catalog.json'),
    import('@inter-ikea-storageone/range-bedroom-storage-propping/data/catalog.json')
  ],
  trofast: () => [import('@inter-ikea-storageone/range-trofast/data/catalog.json')]
};

/**
 * Prefetch all catalogs for a given range
 * @param range - The range identifier (e.g., 'pax', 'besta')
 * @returns Promise that resolves when all catalogs are loaded
 */
export function prefetchCatalogsForRange(range: string): Promise<void> {
  const prefetcher = catalogPrefetchers[range];

  if (!prefetcher) {
    // Range doesn't have catalog prefetching configured, silently skip
    return Promise.resolve();
  }

  return Promise.all(prefetcher())
    .then(() => {
      // Catalogs successfully prefetched
    })
    .catch(() => {
      // Silently ignore catalog preload errors
    });
}
